import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Globe,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { analyticsService, GoogleAnalyticsProperty } from '@/services/analytics';
import { useNavigate } from 'react-router-dom';

const AnalyticsPropertySelector: React.FC = () => {
  const [properties, setProperties] = useState<GoogleAnalyticsProperty[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const availableProperties = await analyticsService.getAvailableProperties();
      setProperties(availableProperties);
      
      // Auto-select first property and its first view if available
      if (availableProperties.length > 0) {
        const firstProperty = availableProperties[0];
        setSelectedProperty(firstProperty.id);
        setSelectedView(firstProperty.view_id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Google Analytics properties');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConnection = async (propertyId?: string, viewId?: string) => {
    const targetPropertyId = propertyId || selectedProperty;
    const targetViewId = viewId || selectedView || '';
    
    if (!targetPropertyId) {
      setError('Please select a property and view');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      console.log('Saving connection:', { propertyId: targetPropertyId, viewId: targetViewId });
      
      await analyticsService.saveSelectedProperty(targetPropertyId, targetViewId);
      
      console.log('Connection saved successfully');
      
      // Redirect to dashboard or show success message
      navigate('/');
    } catch (err: any) {
      console.error('Failed to save connection:', err);
      setError(err.message || 'Failed to save connection');
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your Google Analytics properties...</p>
        </div>
      </div>
    );
  }

  if (error && !properties.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-4 text-destructive" />
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={loadProperties} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight">Choose Your Website</h1>
        <p className="text-muted-foreground mt-2">
          Select which Google Analytics property to connect for your organization
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          One connection per organization • This will replace any existing connection
        </p>
      </div>

      {/* Properties Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <th className="text-left p-4 font-medium">Property</th>
                  <th className="text-left p-4 font-medium">Website</th>
                  <th className="text-left p-4 font-medium">View</th>
                  <th className="text-left p-4 font-medium">Property ID</th>
                  <th className="text-center p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((property, index) => {
                  const uniqueKey = `${property.id}-${property.view_id}`;
                  const isSelected = selectedProperty === property.id && selectedView === property.view_id;
                  
                  return (
                    <tr 
                      key={uniqueKey}
                      className={`border-b cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-primary/5 border-primary/20' 
                          : 'hover:bg-muted/30'
                      }`}
                      onClick={() => {
                        setSelectedProperty(property.id);
                        setSelectedView(property.view_id);
                      }}
                    >
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Globe className="h-4 w-4 text-primary" />
                          <span className="font-medium">{property.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {property.website_url || 'Not specified'}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {property.view_name}
                      </td>
                      <td className="p-4 text-sm font-mono text-muted-foreground">
                        {property.id}
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {isSelected ? (
                            <>
                              <CheckCircle className="h-5 w-5 text-green-500" />
                              <Badge variant="default" className="bg-green-500">
                                Selected
                              </Badge>
                            </>
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                          )}
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProperty(property.id);
                              setSelectedView(property.view_id);
                              handleSaveConnection(property.id, property.view_id);
                            }}
                            disabled={isSaving}
                            className="ml-2"
                          >
                            {isSaving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Connect'
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4 pt-6">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
        >
          Back to Dashboard
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="text-center">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-muted/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Important Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-1 text-muted-foreground">
            <li>• Only one Google Analytics connection is allowed per organization</li>
            <li>• Connecting a new property will replace any existing connection</li>
            <li>• We only request read-only access to your analytics data</li>
            <li>• You can change your connection anytime from settings</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPropertySelector;
